// Authentication service using Firebase Anonymous Auth
import {
  signInAnonymously,
  onAuthStateChanged,
  EmailAuthProvider,
  GoogleAuthProvider,
  linkWithCredential,
  signInWithEmailAndPassword,
  signInWithPopup,
  sendEmailVerification,
  createUserWithEmailAndPassword,
  sendPasswordResetEmail,
  updateProfile
} from 'firebase/auth';
import { auth } from '../firebase/config';

// Sign in anonymously (creates a persistent uid for this device)
export const signInAnonymous = async () => {
  try {
    const userCredential = await signInAnonymously(auth);
    return userCredential.user;
  } catch (error) {
    console.error('Error signing in anonymously:', error);
    throw error;
  }
};

// Get current authenticated user
export const getCurrentUser = () => {
  return auth.currentUser;
};

// Listen to auth state changes
export const onAuthChange = (callback) => {
  return onAuthStateChanged(auth, callback);
};

// Get the current user's UID (ensures user is signed in)
export const ensureAuthenticated = async () => {
  const user = getCurrentUser();
  if (user) {
    return user.uid;
  }

  // Sign in anonymously if not authenticated
  const newUser = await signInAnonymous();
  return newUser.uid;
};

// Link anonymous account to email/password
export const linkAnonymousToEmail = async (email, password) => {
  const user = getCurrentUser();

  if (!user) {
    throw new Error('No user is currently signed in');
  }

  if (!user.isAnonymous) {
    throw new Error('User is already linked to an account');
  }

  const oldAuthUid = user.uid;

  try {
    const credential = EmailAuthProvider.credential(email, password);
    const userCredential = await linkWithCredential(user, credential);
    const newAuthUid = userCredential.user.uid;

    // Migrate all player tokens from old anonymous UID to new email UID
    try {
      const { migrateTokensToNewAuthUid } = await import('./tokenService');
      const tokensUpdated = await migrateTokensToNewAuthUid(oldAuthUid, newAuthUid);
      console.log(`Migrated ${tokensUpdated} tokens from ${oldAuthUid} to ${newAuthUid}`);
    } catch (tokenError) {
      console.error('Error migrating tokens:', tokenError);
      // Don't throw - email is still linked, user can re-join competitions if needed
    }

    // Send email verification with action code settings
    try {
      const actionCodeSettings = {
        url: window.location.origin + '/profile',
        handleCodeInApp: false
      };
      await sendEmailVerification(userCredential.user, actionCodeSettings);
      console.log('Verification email sent successfully to:', email);
    } catch (emailError) {
      console.error('Failed to send verification email:', emailError);
      // Don't throw - account is still linked even if email fails
      throw new Error(`Account linked but failed to send verification email: ${emailError.message}`);
    }

    return userCredential.user;
  } catch (error) {
    if (error.code === 'auth/email-already-in-use') {
      throw new Error('This email is already registered. Please sign in instead.');
    }
    console.error('Error linking anonymous account:', error);
    throw error;
  }
};

// Sign in with email and password
export const signInWithEmail = async (email, password) => {
  try {
    const userCredential = await signInWithEmailAndPassword(auth, email, password);
    return userCredential.user;
  } catch (error) {
    if (error.code === 'auth/user-not-found' || error.code === 'auth/wrong-password') {
      throw new Error('Invalid email or password');
    }
    throw error;
  }
};

// Check if current user is anonymous
export const isAnonymous = () => {
  const user = getCurrentUser();
  return user ? user.isAnonymous : false;
};

// Check if current user email is verified
export const isEmailVerified = () => {
  const user = getCurrentUser();
  return user ? user.emailVerified : false;
};

// Resend verification email
export const resendVerificationEmail = async () => {
  const user = getCurrentUser();

  if (!user) {
    throw new Error('No user is currently signed in');
  }

  if (user.isAnonymous) {
    throw new Error('Anonymous users cannot receive verification emails');
  }

  if (!user.email) {
    throw new Error('User does not have an email address');
  }

  try {
    const actionCodeSettings = {
      url: window.location.origin + '/profile',
      handleCodeInApp: false
    };
    await sendEmailVerification(user, actionCodeSettings);
    console.log('Verification email resent successfully to:', user.email);
  } catch (error) {
    console.error('Error sending verification email:', error);
    throw new Error(`Failed to send verification email: ${error.message}`);
  }
};

// Link anonymous account to Google
export const linkAnonymousToGoogle = async () => {
  const user = getCurrentUser();

  if (!user) {
    throw new Error('No user is currently signed in');
  }

  if (!user.isAnonymous) {
    throw new Error('User is already linked to an account');
  }

  const oldAuthUid = user.uid;

  try {
    const provider = new GoogleAuthProvider();
    const userCredential = await linkWithCredential(user, await signInWithPopup(auth, provider).then(result =>
      GoogleAuthProvider.credentialFromResult(result)
    ));
    const newAuthUid = userCredential.user.uid;

    // Migrate all player tokens from old anonymous UID to new Google UID
    try {
      const { migrateTokensToNewAuthUid } = await import('./tokenService');
      const tokensUpdated = await migrateTokensToNewAuthUid(oldAuthUid, newAuthUid);
      console.log(`Migrated ${tokensUpdated} tokens from ${oldAuthUid} to ${newAuthUid}`);
    } catch (tokenError) {
      console.error('Error migrating tokens:', tokenError);
      // Don't throw - Google is still linked, user can re-join competitions if needed
    }

    return userCredential.user;
  } catch (error) {
    if (error.code === 'auth/credential-already-in-use') {
      throw new Error('This Google account is already in use. Please sign in instead.');
    }
    console.error('Error linking Google account:', error);
    throw error;
  }
};

// Sign in with Google (for existing Google accounts)
export const signInWithGoogle = async () => {
  try {
    const provider = new GoogleAuthProvider();
    const userCredential = await signInWithPopup(auth, provider);
    return userCredential.user;
  } catch (error) {
    if (error.code === 'auth/popup-closed-by-user') {
      throw new Error('Sign-in cancelled');
    }
    if (error.code === 'auth/popup-blocked') {
      throw new Error('Popup was blocked by the browser. Please allow popups for this site.');
    }
    console.error('Error signing in with Google:', error);
    throw error;
  }
};

// Sign out current user
export const signOut = async () => {
  try {
    await auth.signOut();
    console.log('User signed out successfully');
  } catch (error) {
    console.error('Error signing out:', error);
    throw error;
  }
};

// Delete current user account
export const deleteCurrentUser = async () => {
  const user = getCurrentUser();

  if (!user) {
    throw new Error('No user is currently signed in');
  }

  try {
    await user.delete();
    console.log('User account deleted successfully');
  } catch (error) {
    console.error('Error deleting user account:', error);
    if (error.code === 'auth/requires-recent-login') {
      throw new Error('For security reasons, you need to sign out and sign back in before deleting your account.');
    }
    throw error;
  }
};

// Create account for another user (superuser function)
// Creates account with temporary password, then signs back in as the superuser
export const createAccountForUser = async (email, displayName, tempPassword, superuserEmail, superuserPassword) => {
  // Store current user info to sign back in
  const currentUser = getCurrentUser();
  if (!currentUser) {
    throw new Error('You must be signed in to create accounts');
  }

  try {
    // Create the new user account
    const userCredential = await createUserWithEmailAndPassword(auth, email, tempPassword);
    const newUser = userCredential.user;

    // Set display name
    if (displayName) {
      await updateProfile(newUser, { displayName });
    }

    // Send password reset email so user can set their own password
    await sendPasswordResetEmail(auth, email, {
      url: window.location.origin,
      handleCodeInApp: false
    });

    // Get the new user's UID before signing out
    const newUid = newUser.uid;

    // Sign out the newly created user
    await auth.signOut();

    // Sign back in as the superuser
    await signInWithEmailAndPassword(auth, superuserEmail, superuserPassword);

    return {
      uid: newUid,
      email,
      displayName
    };
  } catch (error) {
    // Try to sign back in as superuser if something went wrong
    try {
      await signInWithEmailAndPassword(auth, superuserEmail, superuserPassword);
    } catch (e) {
      console.error('Failed to restore superuser session:', e);
    }

    if (error.code === 'auth/email-already-in-use') {
      throw new Error('This email is already registered');
    }
    if (error.code === 'auth/invalid-email') {
      throw new Error('Invalid email address');
    }
    if (error.code === 'auth/weak-password') {
      throw new Error('Password is too weak');
    }
    throw error;
  }
};

// Send password reset email to any user (superuser function)
export const sendPasswordResetToUser = async (email) => {
  try {
    await sendPasswordResetEmail(auth, email, {
      url: window.location.origin,
      handleCodeInApp: false
    });
    return true;
  } catch (error) {
    if (error.code === 'auth/user-not-found') {
      throw new Error('No account found with this email');
    }
    throw error;
  }
};
